/**
 * core.js -- thin JS wrapper around the compiled libretro snes9x core.
 *
 * Responsibilities:
 *   - load the emcc-generated ES module (or the stub, see stub-core.js)
 *   - expose the exported C functions as ordinary JS methods
 *   - own the typed-array views into the wasm heap for video and audio
 *
 * The one subtlety worth knowing: the heap is built with ALLOW_MEMORY_GROWTH,
 * so every ArrayBuffer view over it is invalidated whenever the heap grows
 * (the underlying WebAssembly.Memory is replaced). Nothing here caches a view
 * across calls -- `framebuffer()` and `readAudio()` re-derive them from the
 * module's current HEAP* each time. That is a couple of object allocations per
 * frame and avoids an entire class of "detached ArrayBuffer" bugs.
 */

/** Where core/build.sh writes its output, relative to the site root. */
export const CORE_MODULE_URL = '/wasm/snes9x_core.js';

export class Snes9xCore {
  /**
   * @param {object} module    the instantiated Emscripten module
   * @param {boolean} isStub   true when running against stub-core.js
   */
  constructor(module, isStub = false) {
    this.module = module;
    this.isStub = isStub;

    const m = module;
    const fn = (name) => {
      const f = m[`_${name}`];
      if (typeof f !== 'function') {
        throw new Error(`core export missing: _${name}`);
      }
      return f;
    };

    this._init = fn('core_init');
    this._loadRom = fn('core_load_rom');
    this._runFrame = fn('core_run_frame');
    this._reset = fn('core_reset');
    this._unloadRom = fn('core_unload_rom');
    this._isRomLoaded = fn('core_is_rom_loaded');

    this._fbPtr = fn('core_get_framebuffer_ptr');
    this._fbWidth = fn('core_get_framebuffer_width');
    this._fbHeight = fn('core_get_framebuffer_height');
    this._fbStride = fn('core_get_framebuffer_stride');

    this._audioPtr = fn('core_get_audio_buffer_ptr');
    this._audioFrames = fn('core_get_audio_buffer_frames');
    this._audioWriteIdx = fn('core_get_audio_write_index');
    this._audioReadIdx = fn('core_get_audio_read_index');
    this._setAudioReadIdx = fn('core_set_audio_read_index');
    this._audioAvailable = fn('core_get_audio_available');
    this._sampleRate = fn('core_get_sample_rate');
    this._fps = fn('core_get_fps');

    this._setInput = fn('core_set_input_state');
    this._clearInput = fn('core_clear_input_state');

    this._serializeSize = fn('core_serialize_size');
    this._serialize = fn('core_serialize');
    this._unserialize = fn('core_unserialize');

    this._cheatReset = fn('core_cheat_reset');
    this._cheatSet = fn('core_cheat_set');

    this._wramPtr = fn('core_get_wram_ptr');
    this._wramSize = fn('core_get_wram_size');
    this._romPtr = fn('core_get_rom_ptr');
    this._romSize = fn('core_get_rom_size');

    this._malloc = fn('malloc');
    this._free = fn('free');
  }

  init() {
    this._init();
  }

  /**
   * Copy a ROM into the wasm heap and hand it to the core.
   * @param {Uint8Array|ArrayBuffer} bytes
   * @returns {boolean} whether the core accepted the cartridge
   */
  loadRom(bytes) {
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    const ptr = this._malloc(data.length);
    if (!ptr) throw new Error(`out of wasm memory allocating ${data.length} bytes for ROM`);
    try {
      this.module.HEAPU8.set(data, ptr);
      return this._loadRom(ptr, data.length) === 1;
    } finally {
      // retro_load_game() copies the cartridge into the core's own memory
      // map, so the staging buffer can go back immediately.
      this._free(ptr);
    }
  }

  runFrame() {
    this._runFrame();
  }

  reset() {
    this._reset();
  }

  unloadRom() {
    this._unloadRom();
  }

  get romLoaded() {
    return this._isRomLoaded() === 1;
  }

  get width() {
    return this._fbWidth();
  }

  get height() {
    return this._fbHeight();
  }

  get sampleRate() {
    return this._sampleRate();
  }

  get fps() {
    return this._fps();
  }

  /**
   * A Uint8ClampedArray view of the current frame, laid out as RGBA rows of
   * `stride` pixels. Only the leftmost `width` pixels of each row are valid.
   */
  framebuffer() {
    const ptr = this._fbPtr();
    const stride = this._fbStride();
    const height = this._fbHeight();
    return {
      pixels: new Uint8ClampedArray(
        this.module.HEAPU8.buffer,
        ptr,
        stride * height * 4,
      ),
      stride,
      width: this._fbWidth(),
      height,
    };
  }

  /**
   * Drain up to `maxFrames` stereo frames out of the core's ring buffer into
   * two Float32Arrays, converting from int16 on the way.
   *
   * Returns the number of frames actually written, which may be fewer than
   * requested (the emulator has not produced them yet) -- the caller is
   * responsible for deciding what to do about the shortfall.
   */
  readAudio(left, right, maxFrames) {
    const capacity = this._audioFrames();
    // The ring indices are free-running uint32_t in C, but wasm hands i32 back
    // to JS as *signed*, so they read as negative once they pass 2^31 (~18h of
    // play at 32kHz). >>> 0 restores the unsigned value; capacity is a power of
    // two that divides 2^32, so the arithmetic below stays correct across the wrap.
    const writeIdx = this._audioWriteIdx() >>> 0;
    let readIdx = this._audioReadIdx() >>> 0;
    let backlog = (writeIdx - readIdx) >>> 0;

    // The producer has lapped the consumer: every slot from the old read
    // position onward has already been overwritten by newer audio one or
    // more times. core_get_audio_available() clamps its *count* to capacity
    // for exactly this case, but does not (and, being a plain getter, can't)
    // correct audio_read_idx itself -- so without this, the loop below would
    // start reading from a stale position and splice in audio from several
    // wraps ago instead of a clean skip. That reads as genuine but
    // temporally-scrambled content (audibly "corrupted", not silent or
    // static), and only heals once enough subsequent reads happen to carry
    // readIdx back within capacity of writeIdx on their own. Resyncing here
    // jumps straight to the oldest position that's still actually intact.
    // Fast forward makes this far easier to hit than it sounds: MAX_LATENCY
    // in audio.js is a REAL-TIME threshold, but during fast forward each
    // real second of backlog holds FAST_FORWARD_MULTIPLIER times more audio
    // *frames* than the same backlog would at normal speed -- so the ring
    // can lap well before that real-time safety valve ever fires.
    if (backlog > capacity) {
      readIdx = (writeIdx - capacity) >>> 0;
      backlog = capacity;
      this._setAudioReadIdx(readIdx);
    }

    const count = Math.min(backlog, maxFrames, left.length, right.length);
    if (count <= 0) return 0;

    // HEAP16 is indexed in 16-bit units, hence the >> 1 on the byte pointer.
    const ptr = this._audioPtr();
    const ring = this.module.HEAP16;
    const base = ptr >> 1;

    for (let i = 0; i < count; i++) {
      const slot = (readIdx + i) % capacity;
      const o = base + slot * 2;
      left[i] = ring[o] / 32768;
      right[i] = ring[o + 1] / 32768;
    }

    readIdx = (readIdx + count) >>> 0;
    this._setAudioReadIdx(readIdx);
    return count;
  }

  /** Stereo frames produced but not yet drained. */
  audioAvailable() {
    return this._audioAvailable();
  }

  /** Throw away buffered audio (used on reset / state load to avoid a stale burst). */
  flushAudio() {
    this._setAudioReadIdx(this._audioWriteIdx() >>> 0);
  }

  /**
   * @param {number} port   controller port (0 = player 1)
   * @param {number} id     RETRO_DEVICE_ID_JOYPAD_* button id
   * @param {number} value  1 pressed, 0 released
   * @param {number} index  device index, 0 for a standard joypad
   */
  setInput(port, id, value, index = 0) {
    this._setInput(port, index, id, value ? 1 : 0);
  }

  clearInput() {
    this._clearInput();
  }

  /** @returns {Uint8Array|null} a copy of the current save state */
  serialize() {
    const size = this._serializeSize();
    if (size <= 0) return null;

    const ptr = this._malloc(size);
    if (!ptr) throw new Error(`out of wasm memory allocating ${size} bytes for save state`);
    try {
      if (this._serialize(ptr, size) !== 1) return null;
      // slice() (not subarray) so the result owns its bytes and survives both
      // the free() below and any later heap growth.
      return this.module.HEAPU8.slice(ptr, ptr + size);
    } finally {
      this._free(ptr);
    }
  }

  /** @param {Uint8Array|ArrayBuffer} state */
  unserialize(state) {
    const data = state instanceof Uint8Array ? state : new Uint8Array(state);
    const ptr = this._malloc(data.length);
    if (!ptr) throw new Error(`out of wasm memory allocating ${data.length} bytes for save state`);
    try {
      this.module.HEAPU8.set(data, ptr);
      return this._unserialize(ptr, data.length) === 1;
    } finally {
      this._free(ptr);
    }
  }

  /** Clear every cheat the core currently knows about. */
  resetCheats() {
    this._cheatReset();
  }

  /**
   * Apply one Game Genie or Pro Action Replay cheat code. `index` is part of
   * the underlying libretro API but unused for lookup by this core port --
   * every call appends a fresh cheat internally rather than replacing an
   * existing one by index -- so a caller wanting to toggle or remove ONE
   * cheat out of a set must resetCheats() and re-call setCheat() once per
   * still-enabled cheat instead (see web/src/cheats.js, which owns exactly
   * that "reset and replay the enabled set" bookkeeping).
   * @param {number} index position in the batch being (re)applied right now, not a stable id
   * @param {boolean} enabled
   * @param {string} code
   */
  setCheat(index, enabled, code) {
    const bytes = new TextEncoder().encode(code);
    const ptr = this._malloc(bytes.length + 1);
    if (!ptr) throw new Error(`out of wasm memory allocating ${bytes.length + 1} bytes for a cheat code`);
    try {
      this.module.HEAPU8.set(bytes, ptr);
      this.module.HEAPU8[ptr + bytes.length] = 0; // null-terminate for the C side's strcpy()
      this._cheatSet(index, enabled ? 1 : 0, ptr);
    } finally {
      this._free(ptr);
    }
  }

  /**
   * A live, zero-copy Uint8Array view directly over the emulator's 128KB
   * Work RAM (SNES address $7E0000-$7FFFFF), for per-game "editor" plugins
   * -- see web/src/game-editors/. Like framebuffer(), re-derived every call
   * rather than cached, since ALLOW_MEMORY_GROWTH replaces the underlying
   * buffer whenever the heap grows. Writing into this view writes directly
   * into the running game's live memory; there is no separate "poke" call.
   */
  wram() {
    const ptr = this._wramPtr();
    const size = this._wramSize();
    return new Uint8Array(this.module.HEAPU8.buffer, ptr, size);
  }

  /**
   * A live, zero-copy Uint8Array view over the loaded cartridge ROM, as the
   * core itself sees it (post any internal copy) -- used to read the SNES
   * header (title/mapping/checksum) for game-editor-plugin detection. See
   * wram() for the ALLOW_MEMORY_GROWTH caveat, which applies here too.
   * Read-only by convention; nothing in this app writes through it.
   */
  rom() {
    const ptr = this._romPtr();
    const size = this._romSize();
    if (!ptr || size <= 0) return new Uint8Array(0);
    return new Uint8Array(this.module.HEAPU8.buffer, ptr, size);
  }
}

/**
 * Load the emulator core.
 *
 * Tries the real wasm build first and transparently falls back to the stub if
 * it is absent, so the app is usable (UI, storage, input wiring) on a checkout
 * where core/build.sh has not been run. `result.isStub` says which one you got
 * and the UI surfaces it -- a silent fallback would be worse than no fallback.
 *
 * @param {{ forceStub?: boolean }} options
 * @returns {Promise<Snes9xCore>}
 */
export async function loadCore({ forceStub = false } = {}) {
  let stubReason = null; // why the stub was used, if the real core was actually attempted and failed (not just forceStub)
  if (!forceStub) {
    try {
      // The core glue lives in public/, which Vite's dev server deliberately
      // refuses to serve through its module transform pipeline ("should not be
      // imported from source code"). Two things keep it out of that pipeline:
      //
      //   1. @vite-ignore, so import-analysis does not rewrite the specifier
      //      and append its ?import marker (that marker is what triggers the
      //      transform, and the 500 that comes with it);
      //   2. resolving to a fully-qualified absolute URL at runtime, so there
      //      is no bare literal for the analyzer to constant-fold in the
      //      first place.
      //
      // The result is a plain browser fetch of /wasm/snes9x_core.js, which
      // Vite serves as a static file in dev and copies verbatim on build.
      const absoluteUrl = new URL(CORE_MODULE_URL, window.location.origin).href;
      const factory = await import(/* @vite-ignore */ absoluteUrl);
      const create = factory.default ?? factory.createSnes9xCore;
      if (typeof create !== 'function') {
        throw new Error('core module has no default export factory');
      }
      const module = await create();
      return new Snes9xCore(module, false);
    } catch (err) {
      // Surfaced by main.js's boot() into the visible log line, not just this
      // console.warn -- a silent fallback here is especially bad on a device
      // (e.g. a phone) where checking devtools isn't practical. A "core
      // export missing" message specifically means a service-worker-cached
      // core is stale relative to the current JS bundle (see sw.js's
      // CACHE_VERSION comment) -- the JS expects an export an older cached
      // core doesn't have, which throws inside the Snes9xCore constructor.
      stubReason = err?.message ?? String(err);
      console.warn(`[core] real wasm core unavailable (${stubReason}); falling back to stub`);
    }
  }

  const { createStubCore } = await import('./stub-core.js');
  const core = new Snes9xCore(await createStubCore(), true);
  core.stubReason = stubReason;
  return core;
}
